import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { FadeIn, FullScreen, useIsDesktop } from './components/ui';
import Sidebar from './components/Sidebar';
import { useAuth } from './context/AuthContext';
import Home from './screens/Home';
import Packages from './screens/Packages';
import Bookings from './screens/Bookings';
import Profile from './screens/Profile';
import BookingFlow from './screens/BookingFlow';
import StaffJobs from './screens/StaffJobs';
import StaffEarnings from './screens/StaffEarnings';
import ServiceQuestions from './screens/ServiceQuestions';

const customerTabs = [
  { key: 'home', path: '/app', label: 'Home', icon: '🏠' },
  { key: 'packages', path: '/app/packages', label: 'Packages', icon: '📦' },
  { key: 'bookings', path: '/app/bookings', label: 'Bookings', icon: '🗓️' },
  { key: 'profile', path: '/app/profile', label: 'Profile', icon: '👤' },
];

const staffTabs = [
  { key: 'jobs', path: '/app/jobs', label: 'Jobs', icon: '🧰' },
  { key: 'earnings', path: '/app/earnings', label: 'Earnings', icon: '💰' },
  { key: 'profile', path: '/app/profile', label: 'Profile', icon: '👤' },
];

// Phone tab bar: the active tab expands into a glowing pill with icon + label.
function TabItem({ tab, focused, onClick }) {
  return (
    <button className={`tab ${focused ? 'tab--active' : ''}`} onClick={onClick}
      aria-current={focused ? 'page' : undefined} aria-label={tab.label}>
      <span className="tab__icon">{tab.icon}</span>
      {focused && <span className="tab__label">{tab.label}</span>}
    </button>
  );
}

export default function Navigation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const isStaff = user?.role === 'staff';
  const tabs = isStaff ? staffTabs : customerTabs;

  const [bookingPkg, setBookingPkg] = useState(null);
  const [serviceCat, setServiceCat] = useState(null);

  // A tab switch always lands at the top of the new screen.
  useEffect(() => {
    const scroller = document.querySelector('.main');
    if (scroller) scroller.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const homePath = isStaff ? '/app/jobs' : '/app';

  // Home passes a category string → open the service questions; no arg → packages.
  const onHomeBook = (cat) => (cat ? setServiceCat(cat) : navigate('/app/packages'));

  return (
    <div className="layout">
      {isDesktop && (
        <Sidebar tabs={tabs} activePath={location.pathname} onNavigate={navigate} />
      )}

      <main className="main">
        <FadeIn key={location.pathname} from={10} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Routes>
            {isStaff ? (
              <>
                <Route path="jobs" element={<StaffJobs />} />
                <Route path="earnings" element={<StaffEarnings />} />
                <Route path="profile" element={<Profile />} />
              </>
            ) : (
              <>
                <Route path="" element={<Home onBook={onHomeBook} />} />
                <Route path="packages" element={<Packages onBook={setBookingPkg} />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="profile" element={<Profile />} />
              </>
            )}
            <Route path="*" element={<Navigate to={homePath} replace />} />
          </Routes>
        </FadeIn>
      </main>

      {!isDesktop && (
        <div className="tabbar-wrap">
          <nav className="tabbar" aria-label="Main">
            {tabs.map((t) => (
              <TabItem key={t.key} tab={t} focused={location.pathname === t.path}
                onClick={() => navigate(t.path)} />
            ))}
          </nav>
        </div>
      )}

      <FullScreen open={!!bookingPkg} onClose={() => setBookingPkg(null)}>
        {bookingPkg && <BookingFlow pkg={bookingPkg} onClose={() => setBookingPkg(null)} />}
      </FullScreen>

      <FullScreen open={!!serviceCat} onClose={() => setServiceCat(null)}>
        {serviceCat && (
          <ServiceQuestions
            category={serviceCat}
            onClose={() => setServiceCat(null)}
            onSubmitted={() => { setServiceCat(null); navigate('/app/bookings'); }}
          />
        )}
      </FullScreen>
    </div>
  );
}
